import { useState, useEffect, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { TreeStructure, Lightning, Minus, Plus, ArrowsIn, NavigationArrow } from '@phosphor-icons/react';
import { API } from '@/App';
import { toast } from 'sonner';

import { HEALTH_COLORS, EDGE_COLORS, CANVAS_W, CANVAS_H } from './map/constants';
import { computeLayout } from './map/layouts';
import { MapNode, MapEdge } from './map/MapGraphics';
import { NodeDetailPanel, EdgeDetailPanel, ChangeHistoryPanel } from './map/DetailPanels';
import { NLQueryBar } from './map/NLQueryBar';
import { ShareModal } from './map/ShareModal';
import { MiniMap } from './map/MiniMap';
import { MapToolbar, FilterPanel } from './map/MapToolbar';

export default function EnvironmentMap() {
  var [mapData, setMapData] = useState(null);
  var [loading, setLoading] = useState(true);
  var [selectedNode, setSelectedNode] = useState(null);
  var [selectedEdge, setSelectedEdge] = useState(null);
  var [showChangeHistory, setShowChangeHistory] = useState(false);
  var [impactResult, setImpactResult] = useState(null);
  var [positions, setPositions] = useState([]);
  var [layoutMode, setLayoutMode] = useState('force');
  var [searchQuery, setSearchQuery] = useState('');
  var [searchHits, setSearchHits] = useState({ nodes: new Set(), edges: new Set() });
  var [filterCategory, setFilterCategory] = useState('');
  var [filterHealth, setFilterHealth] = useState('');
  var [filterEdgeType, setFilterEdgeType] = useState('');
  var [showFilters, setShowFilters] = useState(false);
  var [dragNode, setDragNode] = useState(null);
  var [zoom, setZoom] = useState(1);
  var [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  var [isPanning, setIsPanning] = useState(false);
  var [panStart, setPanStart] = useState(null);
  var [showShareModal, setShowShareModal] = useState(false);
  var [nlHighlightNodes, setNlHighlightNodes] = useState([]);
  var [nlHighlightEdges, setNlHighlightEdges] = useState([]);
  var [showMiniMap, setShowMiniMap] = useState(true);

  var svgRef = useRef(null);

  var fetchMap = useCallback(function () {
    setLoading(true);
    fetch(API + '/map', { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (data) {
          setMapData(data);
          setPositions(computeLayout(data.nodes, data.edges, CANVAS_W, CANVAS_H, layoutMode));
        }
      })
      .catch(function () { toast.error('Failed to load map'); })
      .finally(function () { setLoading(false); });
  }, [layoutMode]);

  useEffect(function () { fetchMap(); }, [fetchMap]);
  useEffect(function () {
    if (mapData) setPositions(computeLayout(mapData.nodes, mapData.edges, CANVAS_W, CANVAS_H, layoutMode));
  }, [layoutMode, mapData]);

  // Search
  useEffect(function () {
    if (!searchQuery.trim() || !mapData) { setSearchHits({ nodes: new Set(), edges: new Set() }); return; }
    fetch(API + '/map/search?q=' + encodeURIComponent(searchQuery), { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data) setSearchHits({ nodes: new Set(data.matched_nodes), edges: new Set(data.matched_edges) }); })
      .catch(function () {});
  }, [searchQuery, mapData]);

  // Wheel zoom
  useEffect(function () {
    var svgEl = svgRef.current;
    if (!svgEl) return;
    function onWheel(e) { e.preventDefault(); setZoom(function (z) { return Math.max(0.3, Math.min(3, z + (e.deltaY > 0 ? -0.1 : 0.1))); }); }
    svgEl.addEventListener('wheel', onWheel, { passive: false });
    return function () { svgEl.removeEventListener('wheel', onWheel); };
  }, []);

  /* ── Event handlers ── */
  function handleNodeClick(node) { setSelectedNode(node); setSelectedEdge(null); setShowChangeHistory(false); setImpactResult(null); }
  function handleEdgeClick(edge) {
    setSelectedEdge(edge); setSelectedNode(null); setShowChangeHistory(false);
    fetch(API + '/map/edge/' + edge.edge_id, { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data) setSelectedEdge(data); })
      .catch(function () {});
  }
  function handleCanvasClick() { setSelectedNode(null); setSelectedEdge(null); }

  function runImpactAnalysis(nodeId) {
    fetch(API + '/map/impact/' + nodeId, { credentials: 'include' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) { if (data) { setImpactResult(data); toast.info(data.summary); } })
      .catch(function () { toast.error('Impact analysis failed'); });
  }

  function ackChangeEvent(eventId) {
    fetch(API + '/map/changes/' + eventId + '/acknowledge', { method: 'PUT', credentials: 'include' })
      .then(function (r) {
        if (r.ok) {
          setMapData(function (prev) {
            if (!prev) return prev;
            return Object.assign({}, prev, {
              change_events: prev.change_events.map(function (e) {
                return e.event_id === eventId ? Object.assign({}, e, { acknowledged_by: 'me', acknowledged_at: new Date().toISOString() }) : e;
              }),
            });
          });
          toast.success('Acknowledged');
        }
      }).catch(function () {});
  }

  function exportMap(fmt) { window.open(API + '/map/export?fmt=' + fmt, '_blank'); }

  /* ── Drag ── */
  function onDragStart(e, node) { if (layoutMode !== 'manual') return; e.preventDefault(); setDragNode(node.node_id); }
  function onSvgMouseUp() {
    if (!dragNode || !mapData) { setDragNode(null); setIsPanning(false); return; }
    var idx = mapData.nodes.findIndex(function (n) { return n.node_id === dragNode; });
    if (idx >= 0 && positions[idx]) {
      fetch(API + '/map/node/' + dragNode + '/position', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ x: Math.round(positions[idx].x), y: Math.round(positions[idx].y) }) }).catch(function () {});
    }
    setDragNode(null); setIsPanning(false);
  }

  /* ── Pan & Zoom ── */
  function handleSvgMouseDown(e) {
    if (dragNode) return;
    if (e.button === 0 && layoutMode !== 'manual') { setIsPanning(true); setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y }); }
  }
  function handleSvgMouseMove(e) {
    if (dragNode) { onSvgMouseMove_internal(e); return; }
    if (isPanning && panStart) setPanOffset({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
  }
  function handleSvgMouseUp_outer() { if (dragNode) { onSvgMouseUp(); return; } setIsPanning(false); setPanStart(null); }
  function onSvgMouseMove_internal(e) {
    if (!dragNode || !svgRef.current) return;
    var rect = svgRef.current.getBoundingClientRect();
    var nx = (e.clientX - rect.left) * (CANVAS_W / rect.width);
    var ny = (e.clientY - rect.top) * ((CANVAS_H + 40) / rect.height);
    nx = Math.max(80, Math.min(CANVAS_W - 80, nx)); ny = Math.max(50, Math.min(CANVAS_H - 10, ny));
    setPositions(function (prev) {
      if (!mapData) return prev;
      var idx = mapData.nodes.findIndex(function (n) { return n.node_id === dragNode; });
      if (idx < 0) return prev;
      var copy = prev.slice(); copy[idx] = { x: nx, y: ny }; return copy;
    });
  }
  function zoomIn() { setZoom(function (z) { return Math.min(3, z + 0.2); }); }
  function zoomOut() { setZoom(function (z) { return Math.max(0.3, z - 0.2); }); }
  function resetView() { setZoom(1); setPanOffset({ x: 0, y: 0 }); }

  var viewW = CANVAS_W / zoom, viewH = (CANVAS_H + 40) / zoom;
  var viewX = (CANVAS_W - viewW) / 2 - panOffset.x / zoom;
  var viewY = ((CANVAS_H + 40) - viewH) / 2 - panOffset.y / zoom;
  var currentViewBox = viewX + ' ' + viewY + ' ' + viewW + ' ' + viewH;

  function handleMiniMapPan(nx, ny) {
    setPanOffset({ x: ((CANVAS_W - viewW) / 2 - nx) * zoom, y: (((CANVAS_H + 40) - viewH) / 2 - ny) * zoom });
  }
  function handleNlHighlight(nodeIds, edgeIds) { setNlHighlightNodes(nodeIds || []); setNlHighlightEdges(edgeIds || []); }

  var simplified = mapData ? mapData.nodes.length >= 50 : false;

  /* ── Render ── */
  if (loading) return <div className="flex items-center justify-center h-64"><span className="font-mono text-kova-cyan">DERIVING MAP...</span></div>;
  if (!mapData || mapData.nodes.length === 0) {
    return (
      <div className="space-y-4" data-testid="environment-map-page">
        <div><h1 className="font-heading font-bold text-lg">Integration Map</h1><p className="text-[10px] font-mono text-kova-slate mt-0.5">Auto-derived from your KOVA environment</p></div>
        <div className="card-brutal p-12 text-center">
          <TreeStructure size={40} className="text-kova-cyan/12 mx-auto mb-3" />
          <p className="text-kova-slate font-mono text-sm">No systems connected yet</p>
          <p className="text-[10px] text-kova-slate mt-1">Add API connections to see your integration topology</p>
        </div>
      </div>
    );
  }

  var nodes = mapData.nodes, edges = mapData.edges;
  var nodeIndex = {}; nodes.forEach(function (n, i) { nodeIndex[n.node_id] = i; });
  var affectedNodes = impactResult ? new Set(impactResult.affected_nodes) : new Set();
  var affectedEdges = impactResult ? new Set(impactResult.affected_edges) : new Set();
  var hasImpact = affectedNodes.size > 0;
  var hasSearch = searchHits.nodes.size > 0 || searchHits.edges.size > 0;
  var categories = Array.from(new Set(nodes.map(function (n) { return n.category; }).filter(Boolean)));
  var edgeTypes = Array.from(new Set(edges.map(function (e) { return e.edge_type; }).filter(Boolean)));

  var visibleNodeIds = new Set(nodes.filter(function (n) {
    if (filterCategory && n.category !== filterCategory) return false;
    if (filterHealth && n.health_status !== filterHealth) return false;
    return true;
  }).map(function (n) { return n.node_id; }));
  var visibleEdges = edges.filter(function (e) {
    if (filterEdgeType && e.edge_type !== filterEdgeType) return false;
    return visibleNodeIds.has(e.source_node_id) || visibleNodeIds.has(e.target_node_id);
  });

  var changeEvents = mapData.change_events || [];
  var unackedChanges = changeEvents.filter(function (e) { return !e.acknowledged_by; }).length;
  var rightPanel = showChangeHistory ? 'history' : selectedEdge ? 'edge' : selectedNode ? 'node' : null;

  return (
    <div className="space-y-3" data-testid="environment-map-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-lg">Integration Map</h1>
          <p className="text-[10px] font-mono text-kova-slate mt-0.5">Auto-derived from your KOVA environment</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] font-mono text-kova-slate">{mapData.node_count} nodes · {mapData.edge_count} edges</span>
          <span className="text-[9px] font-mono" style={{ color: mapData.health_score >= 0.8 ? '#1D9E75' : mapData.health_score >= 0.5 ? '#C8A030' : '#E24B4A' }}>{Math.round(mapData.health_score * 100)}% health</span>
          {mapData.critical_path_nodes.length > 0 && <span className="text-[8px] font-mono px-1.5 py-0.5 bg-kova-gold/10 text-kova-gold border border-kova-gold/20 uppercase">{mapData.critical_path_nodes.length} critical</span>}
        </div>
      </div>

      {/* Toolbar */}
      <MapToolbar
        layoutMode={layoutMode} setLayoutMode={setLayoutMode}
        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
        showFilters={showFilters} setShowFilters={setShowFilters}
        filterCategory={filterCategory} filterHealth={filterHealth} filterEdgeType={filterEdgeType}
        setShowChangeHistory={setShowChangeHistory} setSelectedNode={setSelectedNode} setSelectedEdge={setSelectedEdge}
        unackedChanges={unackedChanges} setShowShareModal={setShowShareModal}
        exportMap={exportMap} fetchMap={fetchMap} impactResult={impactResult} setImpactResult={setImpactResult}
      />

      {/* Filters */}
      <FilterPanel
        showFilters={showFilters} categories={categories} edgeTypes={edgeTypes}
        filterCategory={filterCategory} setFilterCategory={setFilterCategory}
        filterHealth={filterHealth} setFilterHealth={setFilterHealth}
        filterEdgeType={filterEdgeType} setFilterEdgeType={setFilterEdgeType}
      />

      {/* Impact banner */}
      <AnimatePresence>
        {impactResult && (
          <div className="card-brutal p-3 border-l-2 border-kova-red flex items-start gap-2" data-testid="impact-banner">
            <Lightning size={14} className="text-kova-red shrink-0 mt-0.5" weight="fill" />
            <p className="text-xs text-kova-silv">{impactResult.summary}</p>
          </div>
        )}
      </AnimatePresence>

      {/* Canvas + Detail Panel */}
      <div className="relative card-brutal overflow-hidden" style={{ height: CANVAS_H + 40 }}>
        {layoutMode === 'manual' && <div className="absolute top-2 left-3 z-10 text-[8px] font-mono text-kova-cyan/60 bg-kova-bg/80 px-2 py-1 border border-kova-cyan/20">Drag nodes to position</div>}
        {simplified && <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 text-[8px] font-mono text-kova-gold/80 bg-kova-bg/80 px-2 py-1 border border-kova-gold/20" data-testid="simplified-mode-badge">Simplified view (50+ nodes)</div>}

        {!simplified && <NLQueryBar onResult={function (data) { if (data.action === 'impact_analysis' && data.highlight_nodes && data.highlight_nodes[0]) runImpactAnalysis(data.highlight_nodes[0]); }} onHighlight={handleNlHighlight} />}

        {/* Zoom controls */}
        <div className="absolute top-2 right-2 z-20 flex flex-col gap-0.5" data-testid="zoom-controls">
          <button onClick={zoomIn} className="w-6 h-6 bg-kova-card/90 border border-kova-cyan/12 text-kova-slate hover:text-white flex items-center justify-center transition-colors" data-testid="zoom-in-btn"><Plus size={12} /></button>
          <button onClick={zoomOut} className="w-6 h-6 bg-kova-card/90 border border-kova-cyan/12 text-kova-slate hover:text-white flex items-center justify-center transition-colors" data-testid="zoom-out-btn"><Minus size={12} /></button>
          <button onClick={resetView} className="w-6 h-6 bg-kova-card/90 border border-kova-cyan/12 text-kova-slate hover:text-white flex items-center justify-center transition-colors" title="Reset view" data-testid="zoom-reset-btn"><ArrowsIn size={12} /></button>
          <button onClick={function () { setShowMiniMap(!showMiniMap); }} className={'w-6 h-6 bg-kova-card/90 border border-kova-cyan/12 flex items-center justify-center transition-colors ' + (showMiniMap ? 'text-kova-cyan' : 'text-kova-slate hover:text-white')} title="Toggle mini-map" data-testid="toggle-minimap-btn"><NavigationArrow size={12} /></button>
          <div className="text-[7px] font-mono text-kova-slate text-center mt-0.5" data-testid="zoom-level">{Math.round(zoom * 100)}%</div>
        </div>

        <svg ref={svgRef} width="100%" height={CANVAS_H + 40} viewBox={currentViewBox} className="bg-kova-bg" data-testid="map-canvas"
             style={{ cursor: isPanning ? 'grabbing' : (layoutMode === 'manual' ? 'default' : 'grab') }}
             onClick={handleCanvasClick} onMouseDown={handleSvgMouseDown} onMouseMove={handleSvgMouseMove} onMouseUp={handleSvgMouseUp_outer} onMouseLeave={handleSvgMouseUp_outer}>
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(10,191,163,0.12)" strokeWidth="0.3" />
            </pattern>
          </defs>
          <rect width={CANVAS_W * 3} height={(CANVAS_H + 40) * 3} x={-CANVAS_W} y={-(CANVAS_H + 40)} fill="url(#grid)" />
          {visibleEdges.map(function (e) {
            var si = nodeIndex[e.source_node_id], ti = nodeIndex[e.target_node_id];
            if (si == null || ti == null || !positions[si] || !positions[ti]) return null;
            return <MapEdge key={e.edge_id} x1={positions[si].x} y1={positions[si].y} x2={positions[ti].x} y2={positions[ti].y} edge={e} isAffected={affectedEdges.has(e.edge_id) || nlHighlightEdges.indexOf(e.edge_id) >= 0} isSelected={selectedEdge && selectedEdge.edge_id === e.edge_id} onClick={handleEdgeClick} simplified={simplified} />;
          })}
          {nodes.map(function (n, i) {
            if (!positions[i] || !visibleNodeIds.has(n.node_id)) return null;
            var isAffected = affectedNodes.has(n.node_id);
            var isNlHighlight = nlHighlightNodes.indexOf(n.node_id) >= 0;
            var isDimmed = (hasImpact && !isAffected && impactResult.source_node.node_id !== n.node_id) || (hasSearch && !searchHits.nodes.has(n.node_id)) || (nlHighlightNodes.length > 0 && !isNlHighlight);
            return <MapNode key={n.node_id} node={n} x={positions[i].x} y={positions[i].y} isSelected={selectedNode && selectedNode.node_id === n.node_id} isAffected={isAffected || isNlHighlight} isDimmed={isDimmed} isSearchHit={searchHits.nodes.has(n.node_id)} onClick={handleNodeClick} onDragStart={onDragStart} simplified={simplified} />;
          })}
        </svg>

        {/* Legend */}
        <div className="absolute bottom-2 left-3 flex items-center gap-4 text-[8px] font-mono text-kova-slate">
          {Object.entries(EDGE_COLORS).map(function (e) { return <span key={e[0]} className="flex items-center gap-1"><span className="w-3 h-0.5" style={{ background: e[1] }} />{e[0]}</span>; })}
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 border border-kova-gold" />critical path</span>
        </div>

        {showMiniMap && nodes.length > 3 && <MiniMap nodes={nodes} edges={edges} positions={positions} nodeIndex={nodeIndex} viewBox={currentViewBox} canvasW={CANVAS_W} canvasH={CANVAS_H + 40} onPan={handleMiniMapPan} visibleNodeIds={visibleNodeIds} />}

        <AnimatePresence>
          {rightPanel === 'node' && <NodeDetailPanel node={selectedNode} edges={edges} onClose={function () { setSelectedNode(null); }} onImpact={runImpactAnalysis} />}
          {rightPanel === 'edge' && <EdgeDetailPanel edge={selectedEdge} onClose={function () { setSelectedEdge(null); }} />}
          {rightPanel === 'history' && <ChangeHistoryPanel events={changeEvents} onClose={function () { setShowChangeHistory(false); }} onAck={ackChangeEvent} />}
        </AnimatePresence>
      </div>

      {/* Share Modal */}
      <AnimatePresence>
        {showShareModal && <ShareModal onClose={function () { setShowShareModal(false); }} />}
      </AnimatePresence>

      {/* Isolated nodes */}
      {mapData.isolated_nodes.length > 0 && (
        <div className="space-y-2">
          <span className="text-[10px] font-mono text-kova-slate uppercase tracking-wider">Unconnected ({mapData.isolated_nodes.length})</span>
          <div className="flex flex-wrap gap-2">
            {mapData.isolated_nodes.map(function (nid) {
              var node = nodes.find(function (n) { return n.node_id === nid; });
              if (!node) return null;
              return (
                <button key={nid} onClick={function () { handleNodeClick(node); }} className="flex items-center gap-2 px-3 py-2 border border-kova-cyan/12 hover:border-kova-cyan/30 transition-colors" data-testid={'isolated-' + nid}>
                  <span className="w-2 h-2 rounded-full" style={{ background: HEALTH_COLORS[node.health_status] }} />
                  <span className="text-xs font-mono text-kova-silv">{node.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
